import type { ReactNode } from 'react';
import { cn } from '../../cn';

export interface PageHeaderProps {
  /**
   * Already-translated title string. The single source of page-title
   * typography. Optional: a header may render with only a description
   * (e.g. an embedded detail subtitle) or only breadcrumb/adornment.
   */
  title?: string;
  /** Already-translated supporting copy rendered under the title. */
  description?: ReactNode;
  /**
   * Inline node rendered next to the title — status badges, role chips,
   * count pills, leading glyphs. Keeps such trim inside the header's own
   * slot instead of wrapping PageHeader in a hand-rolled flex row.
   */
  titleAdornment?: ReactNode;
  /**
   * Right-aligned primary action(s) — e.g. a Create button.
   *
   * Bare controls are handed straight in; the row below wraps them. For MORE
   * than a couple, use `PageToolbar` / `PageToolbarGroup` rather than a
   * container of your own — a plain `<div className="flex items-center gap-…">`
   * here does not wrap, which leaves this row one unbreakable max-content child
   * and pins the header wider than a narrow viewport. That is exactly how the
   * dispatch console's eight controls ran off the edge of a phone.
   */
  actions?: ReactNode;
  /**
   * Breadcrumb slot rendered above the title. This PR only exposes the
   * slot; route-derived breadcrumb content is a follow-up (#1286 non-goal).
   */
  breadcrumb?: ReactNode;
  /**
   * Layout alignment. `left` (default) is the app-shell page header with
   * actions on the right. `center` is for standalone card layouts (auth /
   * error pages) that live outside the shell and center their content.
   */
  align?: 'left' | 'center';
  /**
   * Extra classes for the `<h1>`, merged after the default typography. For a
   * page whose design sets a different title size; leave unset otherwise.
   */
  titleClassName?: string;
  className?: string;
}

/**
 * Framework page header (#1286). Collapses the ~30 hand-rolled `<h1>` page
 * headers (and their 5 divergent title styles) into one contract: pages
 * declare content, the framework owns the typography and layout.
 */
export function PageHeader({
  title,
  description,
  titleAdornment,
  actions,
  breadcrumb,
  align = 'left',
  titleClassName,
  className,
}: PageHeaderProps) {
  const centered = align === 'center';
  return (
    <div
      data-testid="page-header"
      className={cn(
        // `flex-wrap` lets the actions block drop to its own line on narrow
        // widths instead of forcing horizontal overflow (the row never
        // shrink-0-pins the actions off-screen).
        'flex flex-wrap gap-4',
        centered ? 'flex-col items-center text-center' : 'items-start justify-between',
        className,
      )}
    >
      <div className={cn('min-w-0 space-y-1', centered && 'flex flex-col items-center')}>
        {breadcrumb}
        {(title != null || titleAdornment != null) && (
          <div className={cn('flex items-center gap-3', centered && 'justify-center')}>
            {title != null && (
              <h1
                className={cn(
                  'font-display text-2xl font-bold tracking-tight text-primary [&:lang(zh)]:tracking-normal',
                  titleClassName,
                )}
              >
                {title}
              </h1>
            )}
            {titleAdornment}
          </div>
        )}
        {description != null && <p className="text-sm text-muted-foreground">{description}</p>}
      </div>
      {actions != null && (
        // No `shrink-0`: `min-w-0` lets the actions block shrink so an inner
        // `flex-wrap` can wrap its controls rather than expand to max-content.
        <div className={cn('flex flex-wrap items-center gap-2', !centered && 'min-w-0')}>
          {actions}
        </div>
      )}
    </div>
  );
}
